/**
 * What opens under a step's row: one view per kind of tool, picked by name (the generic view
 * last). A shell command shows its command and output, a file read what it read, an edit its
 * diff, a search the files it found, a web search its links, a subagent its own steps and
 * report, a plan its checklist, an Orgus tool its answer as a table or a card. Every view ends
 * with the technical details: tool, input, raw answer, audit id.
 */
import { BookOpen, Bot, Check, ChevronRight, Circle, FilePen, FileText, FolderSearch, Globe, ListChecks, ServerCog, SquareTerminal, Wrench } from '@orgus/ui/icons';
import type { LucideIcon } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AssistantStep } from '@orgus/protocol';
import { Button, CopyButton, Markdown, Pressable, cn, formatDate, formatDecimal, formatInt, formatMoney, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@orgus/ui';
import { stepInputJson } from '../model.ts';
import { baseName, hostOf, isOrgusTool, todosOf, toolFamily, toolName } from '../labels.ts';
import type { ToolFamily } from '../labels.ts';
import { keyWords, resultShape } from '../shape.ts';
import { orgusView } from './orgusViews.tsx';
import type { ResultShape } from '../shape.ts';
import type { StepNode } from '../turns.ts';

// ---------------------------------------------------------------------------
// Kit
// ---------------------------------------------------------------------------

/** A scrolling block of text; mono for code and output. */
export function TextBlock({ text, mono, className }: { text: string; mono?: boolean; className?: string }) {
  return <pre className={cn('max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-control bg-raised px-3 py-2 text-caption text-fg-muted', mono && 'font-mono', className)}>{text}</pre>;
}

/** A label that opens what is under it. */
export function Disclosure({ label, children, defaultOpen = false }: { label: string; children: ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  // A quiet caption, not a button: the evidence is there when wanted, never louder than the result.
  return (
    <div className="flex flex-col gap-1">
      <Pressable
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 self-start rounded-control text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg-muted"
      >
        <ChevronRight className={cn('size-3 transition-transform duration-(--duration-fast)', open && 'rotate-90')} aria-hidden />
        {label}
      </Pressable>
      {open && <div className="pl-4">{children}</div>}
    </div>
  );
}

/** The folder every path shares, so a list can say it once: C:/work/orgus/apps/web/src. */
export function commonFolder(paths: readonly string[]): string {
  if (paths.length < 2) return '';
  const split = paths.map((p) => p.replace(/\\/g, '/').split('/').slice(0, -1));
  const first = split[0]!;
  let n = 0;
  while (n < first.length && split.every((s) => s[n] === first[n])) n++;
  return first.slice(0, n).join('/');
}

/** The first `first` items, then a button for the rest. */
export function ShowMore<T>({ items, first = 5, noun, render }: { items: readonly T[]; first?: number; noun: string; render: (item: T, i: number) => ReactNode }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, first);
  const rest = items.length - first;
  return (
    <>
      {shown.map(render)}
      {rest > 0 && (
        <Button variant="ghost" size="sm" className="self-start text-fg-muted" onClick={() => setAll((a) => !a)}>
          {all ? `Show fewer ${noun}` : `Show ${formatInt(rest)} more ${noun}`}
        </Button>
      )}
    </>
  );
}

/** One quiet fact: "In src · 42 lines". */
export function Fact({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'attention' }) {
  return <p className={cn('text-caption', tone === 'attention' ? 'text-status-attention-ink' : 'text-fg-subtle')}>{children}</p>;
}

/** A path as its folder (quiet) and its name. */
export function PathLine({ path, base = '' }: { path: string; base?: string }) {
  const name = baseName(path);
  const full = path.replace(/\\/g, '/');
  // Under a shared base, only the part below it is worth reading.
  const rel = base && full.startsWith(`${base}/`) ? full.slice(base.length + 1) : full;
  const folder = rel.slice(0, Math.max(0, rel.length - name.length)).replace(/\/+$/, '');
  return (
    <span className="flex min-w-0 items-center gap-2 text-caption" title={path}>
      <FileText className="size-3.5 shrink-0 text-fg-subtle" aria-hidden />
      <span className="truncate text-fg">{name}</span>
      {folder && <span className="truncate text-fg-subtle">{folder}</span>}
    </span>
  );
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const lineCount = (t: string) => (t ? t.split('\n').length : 0);
const fmt = { date: (ts: number) => formatDate(ts), number: (n: number) => (Number.isInteger(n) ? formatInt(n) : formatDecimal(n, 2)), money: (usd: number) => formatMoney(usd) };

// ---------------------------------------------------------------------------
// Result shapes
// ---------------------------------------------------------------------------

/** A tool's answer drawn by its shape: a table, a card, a reason, or text. */
export function ShapedResult({ shape }: { shape: ResultShape }) {
  switch (shape.kind) {
    case 'rows':
      return <RowsTable shape={shape} />;
    case 'record':
      return (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1 rounded-card border border-border-subtle px-3 py-2 text-caption sm:grid-cols-2">
          {shape.fields.map(([k, v]) => (
            <div key={k} className="flex min-w-0 gap-2">
              <dt className="shrink-0 text-fg-subtle">{k}</dt>
              <dd className="min-w-0 truncate text-fg" title={v}>
                {v}
              </dd>
            </div>
          ))}
        </dl>
      );
    case 'failure':
      return (
        <p role="note" className="rounded-control border border-status-danger-line bg-status-danger-soft px-3 py-2 text-caption text-fg">
          {shape.message}
        </p>
      );
    case 'text':
      return <TextBlock text={shape.text} mono={/^[\s{[]/.test(shape.text) || shape.text.includes('\n')} />;
    case 'empty':
      return <Fact>Nothing came back.</Fact>;
  }
}

const TABLE_FIRST = 10;

function RowsTable({ shape }: { shape: Extract<ResultShape, { kind: 'rows' }> }) {
  const [all, setAll] = useState(false);
  const rows = all ? shape.rows : shape.rows.slice(0, TABLE_FIRST);
  return (
    <div className="flex flex-col gap-1">
      {shape.meta.length > 0 && <Fact>{shape.meta.map(([k, v]) => `${k} ${v}`).join(' · ')}</Fact>}
      <div className="rounded-card border border-border-subtle">
        <Table>
          <TableHeader>
            <TableRow>
              {shape.columns.map((c, k) => (
                <TableHead key={c} numeric={shape.numeric[k]}>
                  {keyWords(c)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                {shape.columns.map((c, k) => (
                  <TableCell key={c} numeric={shape.numeric[k]} primary={k === 0} className="max-w-64 truncate" title={r[c]}>
                    {r[c]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {shape.rows.length > TABLE_FIRST && (
        <Button variant="ghost" size="sm" className="self-start text-fg-muted" onClick={() => setAll((a) => !a)}>
          {all ? 'Show the first 10 rows' : `Show all ${formatInt(shape.total)} rows`}
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

interface ViewProps {
  node: StepNode;
  /** Draws a subagent's own steps (the activity rows, passed in to avoid a cycle). */
  renderSteps: (nodes: StepNode[]) => ReactNode;
}

function ShellView({ node }: ViewProps) {
  const s = node.step;
  const cmd = str(s.input.command);
  const out = s.result ?? '';
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start gap-2 rounded-control bg-raised px-3 py-2 font-mono text-caption text-fg">
        <span className="select-none text-fg-subtle" aria-hidden>
          $
        </span>
        <span className="min-w-0 flex-1 whitespace-pre-wrap break-all">{cmd}</span>
        <CopyButton text={cmd} label="Copy command" />
      </div>
      {s.outcome === 'running' ? null : out.trim() ? <TextBlock text={out} mono /> : <Fact>It printed nothing.</Fact>}
    </div>
  );
}

function ReadView({ node }: ViewProps) {
  const s = node.step;
  const path = str(s.input.file_path) || str(s.input.notebook_path);
  // Claude Code numbers the lines it shows the model ("   12→text"); people read the text.
  const text = (s.result ?? '').replace(/^ *\d+→/gm, '');
  return (
    <div className="flex flex-col gap-2">
      {path && <PathLine path={path} />}
      {text.trim() ? (
        <>
          <Fact>{formatInt(lineCount(text))} lines read</Fact>
          <Disclosure label="What it read">
            <TextBlock text={text} mono />
          </Disclosure>
        </>
      ) : (
        s.outcome !== 'running' && <Fact>The file was empty.</Fact>
      )}
    </div>
  );
}

/** Removed and added lines, each side capped. */
function Diff({ before, after }: { before: string; after: string }) {
  const CAP = 12;
  const side = (text: string, kind: 'del' | 'add') => {
    const lines = text ? text.split('\n') : [];
    return (
      <>
        {lines.slice(0, CAP).map((l, i) => (
          <div key={`${kind}${i}`} className={cn('flex gap-2 px-3', kind === 'del' ? 'bg-status-danger-soft' : 'bg-status-success-soft')}>
            <span className="select-none text-fg-subtle" aria-hidden>
              {kind === 'del' ? '−' : '+'}
            </span>
            <span className="min-w-0 whitespace-pre-wrap break-all">{l}</span>
          </div>
        ))}
        {lines.length > CAP && <div className="px-3 text-fg-subtle">and {formatInt(lines.length - CAP)} more lines</div>}
      </>
    );
  };
  return (
    <div className="overflow-hidden rounded-control border border-border-subtle py-1 font-mono text-caption text-fg">
      <div aria-label="Removed">{side(before, 'del')}</div>
      <div aria-label="Added">{side(after, 'add')}</div>
    </div>
  );
}

function EditView({ node }: ViewProps) {
  const i = node.step.input;
  const path = str(i.file_path) || str(i.notebook_path);
  const edits = Array.isArray(i.edits) ? (i.edits as Record<string, unknown>[]) : [{ old_string: i.old_string, new_string: i.new_string ?? i.new_source }];
  return (
    <div className="flex flex-col gap-2">
      {path && <PathLine path={path} />}
      {edits.length > 1 && <Fact>{formatInt(edits.length)} edits</Fact>}
      {edits.slice(0, 6).map((e, k) => (
        <Diff key={k} before={str(e.old_string)} after={str(e.new_string)} />
      ))}
    </div>
  );
}

function WriteView({ node }: ViewProps) {
  const content = str(node.step.input.content);
  const path = str(node.step.input.file_path);
  return (
    <div className="flex flex-col gap-2">
      {path && <PathLine path={path} />}
      <Fact>{formatInt(lineCount(content))} lines written</Fact>
      {content && (
        <Disclosure label="What it wrote">
          <TextBlock text={content} mono />
        </Disclosure>
      )}
    </div>
  );
}

/** Grep, Glob and LS: the files they found, or the matching lines. */
function SearchView({ node }: ViewProps) {
  const s = node.step;
  const files = Array.isArray(s.structured?.filenames) ? (s.structured!.filenames as string[]) : null;
  const text = s.result ?? '';
  const list = files ?? (/^Found \d+ files?\n/.test(text) ? text.split('\n').slice(1).filter(Boolean) : null);
  const base = list ? commonFolder(list) : '';
  if (s.outcome === 'running') return null;
  if (list) {
    if (!list.length) return <Fact>No files matched.</Fact>;
    return (
      <div className="flex flex-col gap-1">
        <Fact>
          {list.length === 1 ? '1 file' : `${formatInt(list.length)} files`}
          {base ? (
            <>
              {' in '}
              <span className="font-mono">{base}</span>
            </>
          ) : null}
        </Fact>
        <ShowMore items={list} first={8} noun="files" render={(f) => <PathLine key={f} path={f} base={base} />} />
      </div>
    );
  }
  return text.trim() ? <TextBlock text={text} mono /> : <Fact>No matches.</Fact>;
}

/** Links in a WebSearch answer: "Links: [{"title","url"}]" or markdown links. */
function linksOf(text: string): { title: string; url: string }[] {
  const out: { title: string; url: string }[] = [];
  const json = /Links:\s*(\[[\s\S]*?\])\s*(?:\n|$)/.exec(text);
  if (json) {
    try {
      for (const l of JSON.parse(json[1]!) as { title?: string; url?: string }[]) if (l.url && /^https?:/.test(l.url)) out.push({ title: l.title || hostOf(l.url), url: l.url });
    } catch {
      // Not JSON after all: fall through to markdown links.
    }
  }
  if (!out.length) for (const m of text.matchAll(/\[([^\]]+)\]\((https?:[^)\s]+)\)/g)) out.push({ title: m[1]!, url: m[2]! });
  return out;
}

function WebSearchView({ node }: ViewProps) {
  const links = useMemo(() => linksOf(node.step.result ?? ''), [node.step.result]);
  if (node.step.outcome === 'running') return null;
  if (!links.length) return node.step.result ? <TextBlock text={node.step.result} /> : <Fact>No results.</Fact>;
  return (
    <ul className="flex flex-col gap-1">
      <ShowMore
        items={links}
        noun="results"
        render={(l) => (
          <li key={l.url} className="flex min-w-0 items-center gap-2 text-caption">
            <Globe className="size-3.5 shrink-0 text-fg-subtle" aria-hidden />
            <a href={l.url} target="_blank" rel="noopener noreferrer nofollow" className="truncate text-fg hover:underline">
              {l.title}
            </a>
            <span className="shrink-0 text-fg-subtle">{hostOf(l.url)}</span>
          </li>
        )}
      />
    </ul>
  );
}

function WebFetchView({ node }: ViewProps) {
  const url = str(node.step.input.url);
  return (
    <div className="flex flex-col gap-2">
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer nofollow" className="flex min-w-0 items-center gap-2 text-caption text-fg hover:underline">
          <Globe className="size-3.5 shrink-0 text-fg-subtle" aria-hidden />
          <span className="truncate">{url}</span>
        </a>
      )}
      {node.step.result &&
        // A short finding reads in place; a long one waits behind its label.
        (node.step.result.length <= 400 ? (
          <p className="border-l-2 border-border-subtle pl-3 text-caption text-fg-muted">{node.step.result}</p>
        ) : (
          <Disclosure label="What it found">
            <TextBlock text={node.step.result} />
          </Disclosure>
        ))}
    </div>
  );
}

function AgentView({ node, renderSteps }: ViewProps) {
  return (
    <div className="flex flex-col gap-2">
      {node.children.length > 0 && <div className="border-l border-border-subtle pl-3">{renderSteps(node.children)}</div>}
      {(node.report ?? node.step.result) && (
        <div className="flex flex-col gap-1 border-l-2 border-border-subtle pl-3">
          <Fact>What it reported</Fact>
          <Markdown source={node.report ?? node.step.result ?? ''} size="sm" className="text-caption text-fg-muted" />
        </div>
      )}
    </div>
  );
}

function PlanStepView({ node }: ViewProps) {
  const todos = todosOf(node.step.input);
  const done = todos.filter((t) => t.status === 'completed').length;
  return (
    <div className="flex flex-col gap-2">
      <Fact>
        {formatInt(done)} of {formatInt(todos.length)} done
      </Fact>
      <ol className="flex flex-col gap-1">
        {todos.map((t, i) => (
          <li key={i} className="flex items-start gap-2 text-caption">
            {t.status === 'completed' ? (
              <Check className="mt-1 size-3.5 shrink-0 text-status-success" aria-label="Done" />
            ) : (
              <Circle className={cn('mt-1 size-3.5 shrink-0', t.status === 'in_progress' ? 'fill-accent text-accent' : 'text-fg-subtle')} aria-label={t.status === 'in_progress' ? 'In progress' : 'Not started'} />
            )}
            <span className={cn(t.status === 'in_progress' ? 'font-medium text-fg' : t.status === 'completed' ? 'text-fg-muted' : 'text-fg')}>{t.content}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function OrgusView({ node }: ViewProps) {
  const step = node.step;
  const shape = useMemo(() => resultShape(step.result, fmt), [step.result]);
  if (step.outcome === 'running' || step.outcome === 'pending') return null;
  const api = step.capability === 'api.get' || step.capability === 'api.call';
  const request = api ? (
    <p className="flex min-w-0 items-center gap-2 font-mono text-caption">
      <span className="shrink-0 rounded-control bg-raised px-2 text-fg-muted">{step.capability === 'api.get' ? 'GET' : str(step.input.method).toUpperCase() || 'POST'}</span>
      <span className="truncate text-fg">{str(step.input.path)}</span>
    </p>
  ) : null;
  const own = api ? null : orgusView(step);
  if (own) return own;
  if (step.result === undefined) return step.message ? <Fact>{step.message}</Fact> : request;
  return (
    <div className="flex flex-col gap-2">
      {request}
      <ShapedResult shape={shape} />
    </div>
  );
}

function GenericView({ node }: ViewProps) {
  const shape = useMemo(() => resultShape(node.step.result, fmt), [node.step.result]);
  return node.step.result === undefined ? null : <ShapedResult shape={shape} />;
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export interface ToolTemplate {
  id: string;
  icon: LucideIcon;
  match: (name: string, family: ToolFamily) => boolean;
  View: (p: ViewProps) => ReactNode;
}

/** Tried in order; the generic template matches everything. */
export const TEMPLATES: readonly ToolTemplate[] = [
  { id: 'plan', icon: ListChecks, match: (n) => n === 'TodoWrite', View: PlanStepView },
  { id: 'shell', icon: SquareTerminal, match: (n) => n === 'Bash' || n === 'PowerShell', View: ShellView },
  { id: 'read', icon: FileText, match: (n) => n === 'Read' || n === 'NotebookRead' || n === 'BashOutput', View: ReadView },
  { id: 'edit', icon: FilePen, match: (n) => n === 'Edit' || n === 'MultiEdit' || n === 'NotebookEdit', View: EditView },
  { id: 'write', icon: FilePen, match: (n) => n === 'Write', View: WriteView },
  { id: 'search', icon: FolderSearch, match: (n) => n === 'Grep' || n === 'Glob' || n === 'LS', View: SearchView },
  { id: 'web-search', icon: Globe, match: (n) => n === 'WebSearch', View: WebSearchView },
  { id: 'web-fetch', icon: Globe, match: (n) => n === 'WebFetch', View: WebFetchView },
  { id: 'agent', icon: Bot, match: (n) => n === 'Task' || n === 'Agent', View: AgentView },
  { id: 'skill', icon: BookOpen, match: (n) => n === 'Skill', View: GenericView },
  { id: 'orgus', icon: ServerCog, match: (n) => isOrgusTool(n), View: OrgusView },
  { id: 'generic', icon: Wrench, match: () => true, View: GenericView },
];

export function templateOf(step: AssistantStep): ToolTemplate {
  const name = toolName(step);
  const family = toolFamily(step);
  return TEMPLATES.find((t) => t.match(name, family))!;
}

/** The tool, its input, its raw answer and where it is audited, for anyone checking the work. */
export function TechnicalDetails({ step }: { step: AssistantStep }) {
  const input = stepInputJson(step.input);
  return (
    <Disclosure label="Technical details">
      <div className="flex flex-col gap-2">
        <p className="text-caption text-fg-subtle">
          <code className="font-mono text-fg-muted">{toolName(step)}</code>
          {step.auditId && (
            <>
              {' '}
              audited as <code className="font-mono text-fg-muted">{step.auditId}</code>
            </>
          )}
          {step.approval?.decidedBy && <> · decided by {step.approval.decidedBy}</>}
        </p>
        {input !== '{}' && (
          <div className="flex flex-col gap-1">
            <Fact>What it sent</Fact>
            <TextBlock text={input} mono />
          </div>
        )}
        {step.result !== undefined && step.result !== '' && (
          <div className="flex flex-col gap-1">
            <Fact>What came back</Fact>
            <TextBlock text={step.result} mono />
          </div>
        )}
      </div>
    </Disclosure>
  );
}
